<script setup lang="ts">
import { useId } from "vue";
import { ErrorText } from "../ErrorText";
import { HelperText } from "../HelperText";

interface CheckboxPropsBase {
  /** Disables the checkbox and prevents user interaction. */
  disabled?: boolean;
  /** Displays an optional helper text below the checkbox. */
  helperText?: string;
  /** The name attribute for the checkbox input. */
  name?: string;
}

interface CheckboxPropsError extends CheckboxPropsBase {
  /** Displays the checkbox in an error state. */
  error: boolean;
  /** Displays an optional error message below the checkbox when `error` is true. */
  errorText?: string;
}

interface CheckboxPropsValid extends CheckboxPropsBase {
  error?: boolean;
  errorText?: never;
}

export type CheckboxProps = CheckboxPropsError | CheckboxPropsValid;

const { disabled = false, errorText, helperText } = defineProps<CheckboxProps>();
const modelValue = defineModel<boolean>("modelValue");

const errorTextId = `error-text-${useId()}`;
const helperTextId = `helper-text-${useId()}`;

// `display: contents` removes the wrapper's box but keeps its children rendered normally.
// This allows block-level elements (like divs or tables) inside a <slot> to be valid inside <label>.
// It also enables applying visual states (e.g. "disabled") to the slotted content.
</script>

<template>
  <template v-if="$slots.default">
    <div>
      <label
        class="grid grid-cols-[max-content_auto] gap-x-3 gap-y-0.5 align-baseline"
        data-testid="clr-checkbox-label"
      >
        <input
          v-model="modelValue"
          :aria-describedby="
            [errorText ? errorTextId : null, helperText ? helperTextId : null]
              .filter(Boolean)
              .join(' ')
          "
          class="clr-checkbox disabled:before:shadow-disabled-01 m-0 grid h-5 w-5 appearance-none place-content-center rounded-sm border-2 border-dark-blue bg-base-01 before:h-3 before:w-3 before:scale-0 before:shadow-[inset_1em_1em] before:shadow-dark-blue checked:before:scale-90 disabled:cursor-not-allowed disabled:border-disabled-01 before:motion-reduce:transition-transform"
          :class="{
            'before:shadow-error-01 border-error-01': error,
          }"
          :checked="modelValue"
          :disabled="disabled"
          :name="name"
          type="checkbox"
          data-testid="clr-checkbox-input"
        />
        <!-- The span behaves like it's not there, but still applies styles like `disabled` -->
        <span class="contents" :class="{ 'cursor-not-allowed text-disabled-01': disabled }">
          <slot />
        </span>
        <HelperText
          v-if="helperText"
          :id="helperTextId"
          class="col-start-2"
          :class="{ 'text-disabled-01': disabled }"
          data-testid="clr-checkbox-helper-text"
        >
          {{ helperText }}
        </HelperText>
        <ErrorText
          v-if="!disabled && error && errorText?.trim()"
          :id="errorTextId"
          class="pointer-events-none col-start-2"
          data-testid="clr-checkbox-error-text"
        >
          {{ errorText }}
        </ErrorText>
      </label>
    </div>
  </template>
</template>

<style lang="postcss" scoped>
.clr-checkbox {
  transform: translateY(0.075em);

  &::before {
    /* @apply h-2.5 w-2.5; */
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
  }
}
</style>
